import { ChangeDetectionStrategy, Component, Input, OnDestroy } from '@angular/core';

import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';

import { ExtendedBaseProfileField } from '../../../core/profiles/entities/extended-profile-field.entity';
import { ExtendedProfile } from '../../../core/profiles/entities/extended-profile.entity';
import { ProfileField, ProfileFieldScope } from '../../../core/profiles/entities/profile-field.entity';
import { Profile } from '../../../core/profiles/entities/profile.entity';
import { PartialRecord } from '../../../core/records/entities/partial-record.class';
import { ShProfileConfigService } from '../../services/profile-config.service';

@Component({
    selector: 'sh-profile-readonly-fieldset',
    templateUrl: './profile-readonly-fieldset.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShProfileReadonlyFieldsetComponent implements OnDestroy {
    public readonly profile$: ReplaySubject<ExtendedProfile> = new ReplaySubject<ExtendedProfile>(1);
    public profileFields$!: Observable<(ProfileField | ExtendedBaseProfileField)[]>;
    public profileName$!: Observable<string>;

    @Input() record!: PartialRecord;

    @Input() aiProfile?: Profile;

    @Input() set profile(profile: ExtendedProfile) {
        if (profile) {
            this.profile$.next(profile);
        }
    }

    constructor(private readonly configService: ShProfileConfigService) {}

    ngOnInit(): void {
        this.initializeProfile();
    }

    ngOnDestroy(): void {
        this.profile$.complete();
    }

    public trackByIdentity = (index: number, item: ProfileField) => item.FieldDefinitionId;

    private initializeProfile(): void {
        const allProfileFields$: Observable<ExtendedBaseProfileField[]> = this.profile$.pipe(
            map(profile => profile.Fields)
        );
        this.profileFields$ = combineLatest([allProfileFields$, this.canViewStandard$, this.canViewAdvanced$]).pipe(
            map(([fields, standard, advanced]) => fields.filter(field => this.canViewField(field, standard, advanced)))
        );
    }

    public canViewField(field: ExtendedBaseProfileField, canViewStandard: boolean, canViewAdvanced: boolean): boolean {
        if (field.Scope === ProfileFieldScope.Standard) {
            return canViewStandard;
        }

        if (field.Scope === ProfileFieldScope.Advanced) {
            return canViewAdvanced;
        }

        return true;
    }

    public get canViewStandard$(): Observable<boolean> {
        return this.configService.canViewStandard();
    }

    public get canEditStandard$(): Observable<boolean> {
        return this.configService.canEditStandard();
    }

    public get canViewAdvanced$(): Observable<boolean> {
        return this.configService.canViewAdvanced();
    }

    public get canEditAdvanced$(): Observable<boolean> {
        return this.configService.canEditAdvanced();
    }
}
